// The Connections widget's one picture of everything that connects (Connections review, 22 Oct): what the board
// itself connects to (Connect's apps, saved keys, Google) and what can get into it (agent, push and runner keys, and
// apps approved on the Allow screen), then each computer's Claude Code and Codex servers as it last read them. Each
// row says what it is, how it is, whether it can change things, where it is set and when it was last used; the flags
// pick out what fails, waits for a sign-in, is doubled up or sits unused. Nothing here holds a key: the board never
// sends one, and the computers never send theirs (cleanConnections). Pure, so tests/connections-report.test.ts reads it.

import type { GoogleStatus } from "../../shared/googleAnalytics";
import { keyService } from "../../shared/keyServices";
import type { AgentServer, ApiKey, ComputerConnections, Connection, ServiceKey } from "../../shared/types";
import { DAY } from "../../shared/period";

export type ConnState = "ok" | "signin" | "failed" | "off" | "idle";

/** A line to copy and run on that computer, and where to run it. */
export interface ConnCommand {
  label: string;
  line: string;
  /** a project's own server: the folder to run it in */
  folder?: string;
  /** removes it: the view asks before copying */
  removes?: boolean;
}

/** What the view can do itself: Connect's own actions, each after a confirm when it takes something away. */
export type ConnFix =
  | { kind: "signin-app"; id: string }
  | { kind: "remove-app"; id: string }
  | { kind: "change-service"; id: string }
  | { kind: "remove-service"; id: string }
  | { kind: "revoke-key"; id: string }
  | { kind: "unlink-computer"; id: string }
  | { kind: "disconnect-google" }
  | { kind: "disconnect-search-console" };

export interface ConnRow {
  id: string;
  /** "board", or the computer's id */
  place: string;
  /** its group in that place: "Apps the board reads from", "Claude Code" */
  part: string;
  name: string;
  kind: "mcp" | "key";
  state: ConnState;
  stateText: string;
  /** reads only, or can change things; null when nothing says */
  power: "reads" | "changes" | null;
  powerText: string | null;
  where: string;
  lastUsed: number | null;
  /** what it said when it failed, or what it waits for */
  reason: string | null;
  tools: { name: string; reads: boolean }[];
  toolCount: number;
  scope: string | null;
  host: string | null;
  program: string | null;
  project: string | null;
  fixes: ConnFix[];
  commands: ConnCommand[];
  /** where a connector of an account is managed, when no command reaches it */
  manage: { label: string; href: string } | null;
  /** the words search matches */
  text: string;
}

export interface ConnPlace {
  id: string;
  name: string;
  kind: "board" | "computer";
  online: boolean;
  at: number | null;
  looking: number | null;
  reads: boolean;
  agents: ComputerConnections["agents"];
}

export interface ConnFlag {
  id: string;
  kind: "failed" | "signin" | "doubled" | "unused";
  title: string;
  detail: string;
  place: string;
  rows: string[];
}

export interface ConnReport {
  places: ConnPlace[];
  rows: ConnRow[];
  flags: ConnFlag[];
  total: number;
  needYou: number;
  /** red only when something fails, amber while something waits for a sign-in, calm otherwise */
  tone: "bad" | "warn" | "calm";
}

export interface ConnInput {
  now: number;
  connections: Connection[];
  services: ServiceKey[];
  keys: ApiKey[];
  google: GoogleStatus | null;
  searchConsole: GoogleStatus | null;
  /** null until the board has said */
  computers: ComputerConnections[] | null;
}

/** a key that has not been used for this long is flagged */
export const UNUSED_MS = 30 * DAY;

const AGENT_NAME = { claude: "Claude Code", codex: "Codex" } as const;
const SEVERITY: Record<ConnState, number> = { failed: 0, signin: 1, idle: 2, ok: 3, off: 4 };

const hostOf = (url: string): string | null => {
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
};

/**
 * A name as a shell takes it, in double quotes, when it holds only letters, digits and plain punctuation: anything else
 * could end the quotes in some shell (PowerShell reads curly quotes as quotes, security review 22 Oct) and gets no command.
 */
const quoted = (name: string): string | null => (/^[A-Za-z0-9 _.:@/+-]+$/.test(name) ? `"${name}"` : null);

function row(r: Partial<ConnRow> & Pick<ConnRow, "id" | "place" | "part" | "name" | "kind" | "state" | "stateText" | "where">): ConnRow {
  const out: ConnRow = { power: null, powerText: null, lastUsed: null, reason: null, tools: [], toolCount: 0, scope: null, host: null, program: null, project: null, fixes: [], commands: [], manage: null, text: "", ...r };
  out.toolCount = r.toolCount ?? out.tools.length;
  out.text = [out.name, out.part, out.where, out.host, out.program, out.project, out.stateText, out.reason, ...out.tools.map((t) => t.name)].filter(Boolean).join(" ").toLowerCase();
  return out;
}

/** What the board itself connects to, and what can get into it. */
function boardRows(input: ConnInput): ConnRow[] {
  const rows: ConnRow[] = [];
  for (const c of input.connections) {
    const state: ConnState = c.status === "ok" ? "ok" : c.status === "error" ? "failed" : "signin";
    rows.push(
      row({
        id: `app:${c.id}`,
        place: "board",
        part: "Apps the board reads from",
        name: c.name,
        kind: "mcp",
        state,
        stateText: state === "ok" ? "Connected" : c.status === "needs_key" ? "Needs a key" : state === "signin" ? "Needs a sign-in" : "Failing",
        // the board runs only the tools that read, whatever the server has
        power: "reads",
        powerText: "The board only runs the tools that read",
        where: "Connect",
        lastUsed: c.lastUsedAt,
        reason: state === "ok" ? null : c.error,
        tools: c.tools.map((t) => ({ name: t.name, reads: t.reads })),
        host: hostOf(c.url),
        fixes: [...(c.auth === "oauth" && c.status !== "ok" && c.status !== "needs_key" ? [{ kind: "signin-app" as const, id: c.id }] : []), { kind: "remove-app", id: c.id }],
      }),
    );
  }
  for (const k of input.services) {
    const s = keyService(k.id);
    if (!s) continue;
    const bad = k.status === "error";
    rows.push(
      row({
        id: `service:${k.id}`,
        place: "board",
        part: "Saved keys",
        name: s.name,
        kind: "key",
        state: bad ? "failed" : "ok",
        stateText: bad ? "Key not accepted" : "Connected",
        power: "reads",
        powerText: `The board reads ${s.name} with your ${s.noun}`,
        where: "Connect",
        reason: bad ? (k.error ?? `${s.name} stopped accepting the saved ${s.noun}.`) : null,
        fixes: [
          { kind: "change-service", id: k.id },
          { kind: "remove-service", id: k.id },
        ],
      }),
    );
  }
  for (const [g, name, fix] of [
    [input.google, "Google Analytics", "disconnect-google"],
    [input.searchConsole, "Search Console", "disconnect-search-console"],
  ] as const) {
    if (!g?.connected) continue;
    rows.push(
      row({
        id: `google:${fix}`,
        place: "board",
        part: "Google",
        name,
        kind: "key",
        state: g.error ? "failed" : "ok",
        stateText: g.error ? "Failing" : "Connected",
        power: "reads",
        powerText: "Read only, as Google was asked",
        where: "Connect, signed in with Google",
        reason: g.error,
        fixes: [{ kind: fix }],
      }),
    );
  }
  for (const k of input.keys) {
    const what = k.kind === "push" ? "Send-only keys" : k.kind === "runner" ? "Computers" : k.via === "signin" ? "Apps you allowed" : "Agent keys";
    const used = !!k.lastUsedAt;
    rows.push(
      row({
        id: `key:${k.id}`,
        place: "board",
        part: what,
        name: k.label,
        kind: "key",
        state: used || k.via === "signin" ? "ok" : "idle",
        stateText: used ? (k.kind === "runner" ? "Linked" : "In use") : k.via === "signin" ? "Signed in" : "Not used yet",
        power: "changes",
        powerText: k.kind === "push" ? "Sends data to the board's widgets, nothing else" : k.kind === "runner" ? `Runs the board's chat on ${k.host ?? "that computer"}` : "Reads and changes your boards",
        where: k.via === "signin" ? "The Allow screen" : k.kind === "runner" ? "Its set-up line" : "Connect",
        lastUsed: k.lastUsedAt,
        fixes: [k.kind === "runner" ? { kind: "unlink-computer", id: k.id } : { kind: "revoke-key", id: k.id }],
      }),
    );
  }
  return rows;
}

/** Claude Code's and Codex's own lines for a server: sign in, see why, take it away. */
function commandsFor(s: AgentServer): { commands: ConnCommand[]; manage: ConnRow["manage"] } {
  const name = quoted(s.name);
  const commands: ConnCommand[] = [];
  let manage: ConnRow["manage"] = null;
  const folder = s.scope === "local" || s.scope === "project" ? s.project : undefined;
  if (s.agent === "claude") {
    if (s.status === "signin" && name) commands.push({ label: "Sign in", line: `claude mcp login ${name}`, ...(folder ? { folder } : {}) });
    if (s.status === "failed" && name && s.scope !== "account") commands.push({ label: "See why it fails", line: `claude mcp get ${name}`, ...(folder ? { folder } : {}) });
    if (s.scope === "account") manage = { label: "Manage it in claude.ai", href: "https://claude.ai/settings/connectors" };
    else if (s.scope === "plugin") {
      const plugin = /^plugin:([^:]+):/.exec(s.name)?.[1];
      const p = plugin ? quoted(plugin) : null;
      if (p) commands.push({ label: `Turn off the ${plugin} plugin, with all its servers`, line: `claude plugin disable ${p}`, removes: true });
    } else if (name && s.scope !== "managed") commands.push({ label: "Remove it", line: `claude mcp remove ${name} -s ${s.scope}`, removes: true, ...(folder ? { folder } : {}) });
  } else {
    if (s.status === "signin" && name) commands.push({ label: "Sign in", line: `codex mcp login ${name}` });
    if (s.scope === "account") manage = { label: "Manage it in ChatGPT", href: "https://chatgpt.com/#settings/Connectors" };
    else if (s.scope !== "plugin" && name) commands.push({ label: "Remove it", line: `codex mcp remove ${name}`, removes: true });
  }
  return { commands, manage };
}

function computerRows(c: ComputerConnections): ConnRow[] {
  return c.servers.map((s, i) => {
    const state: ConnState = s.status === "connected" ? "ok" : s.status === "unchecked" ? "idle" : s.status;
    const writes = s.tools.some((t) => !t.reads);
    const { commands, manage } = commandsFor(s);
    return row({
      id: `server:${c.id}:${s.agent}:${i}`,
      place: c.id,
      part: AGENT_NAME[s.agent],
      name: s.name,
      kind: "mcp",
      state,
      stateText: state === "ok" ? "Connected" : state === "signin" ? "Needs a sign-in" : state === "failed" ? "Failing" : state === "idle" ? (/^Not approved/.test(s.reason ?? "") ? "Not approved" : "Not checked") : "Off",
      power: s.tools.length ? (writes ? "changes" : "reads") : null,
      powerText: s.tools.length ? (writes ? `${s.tools.filter((t) => !t.reads).length} of its ${s.toolCount ?? s.tools.length} tools can change things` : "Every tool only reads") : null,
      where: s.where,
      reason: state === "failed" ? (s.reason ?? "It did not say why.") : state === "signin" ? "Waiting for you to sign in." : state === "idle" ? (s.reason ?? null) : null,
      tools: s.tools,
      toolCount: s.toolCount ?? s.tools.length,
      scope: s.scope,
      host: s.host ?? null,
      program: s.program ? [s.program, s.package].filter(Boolean).join(" ") : null,
      project: s.project ?? null,
      commands,
      manage,
    });
  });
}

// --- doubled up: one agent with two or more working ways into the same service

/** Services people reach more than one way, by the words in a server's name, or in two or more of its tools' names. */
const SERVICES: { label: string; words: string[] }[] = [
  { label: "Gmail", words: ["gmail"] },
  { label: "Google Calendar", words: ["google calendar", "gcal", "calendar"] },
  { label: "Google Drive", words: ["google drive", "gdrive", "drive"] },
  { label: "Search Console", words: ["search console", "gsc", "searchconsole"] },
  { label: "Google Analytics", words: ["google analytics", "ga4"] },
  { label: "Bookmarks", words: ["bookmarks", "bookmark"] },
  { label: "Slack", words: ["slack"] },
  { label: "Notion", words: ["notion"] },
  { label: "Linear", words: ["linear"] },
  { label: "GitHub", words: ["github"] },
  { label: "Jira", words: ["jira", "atlassian"] },
  { label: "HubSpot", words: ["hubspot"] },
  { label: "ClickUp", words: ["clickup"] },
  { label: "Canva", words: ["canva"] },
  { label: "Postiz", words: ["postiz"] },
  { label: "Stripe", words: ["stripe"] },
  { label: "Lemon Squeezy", words: ["lemon squeezy", "lemonsqueezy"] },
  { label: "Figma", words: ["figma"] },
  { label: "Sentry", words: ["sentry"] },
  { label: "Supabase", words: ["supabase"] },
];

const words = (s: string) =>
  ` ${s
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join(" ")} `;
const has = (text: string, w: string) => text.includes(` ${w} `);

/** The services a server is a way into: by its name, its host, or two or more of its tools. */
export function servicesOf(r: Pick<ConnRow, "name" | "host" | "tools">): string[] {
  const name = words(`${r.name} ${r.host ?? ""}`);
  const tools = r.tools.map((t) => words(t.name));
  return SERVICES.filter((s) => s.words.some((w) => has(name, w) || tools.filter((t) => has(t, w)).length >= 2)).map((s) => s.label);
}

/** A server's name without where it comes from: "claude.ai Gmail" is Gmail, "plugin:productivity:slack" is slack. */
const bareName = (name: string) => name.replace(/^claude\.ai\s+/i, "").replace(/^plugin:[^:]+:/, "");

/** The services reached two or more ways among servers one session would have together. */
function waysIn(list: ConnRow[]): Map<string, Set<ConnRow>> {
  const ways = new Map<string, Set<ConnRow>>();
  const add = (label: string, r: ConnRow) => ways.set(label, (ways.get(label) ?? new Set()).add(r));
  for (const r of list) for (const label of servicesOf(r)) add(label, r);
  // the same server twice, by its host, whatever each is called; never the one host every ChatGPT app shares
  const byHost = new Map<string, ConnRow[]>();
  for (const r of list) if (r.host && !(r.scope === "account" && r.part === AGENT_NAME.codex)) byHost.set(r.host, [...(byHost.get(r.host) ?? []), r]);
  for (const same of byHost.values()) if (same.length > 1) for (const r of same) add(servicesOf(same[0])[0] ?? bareName(same[0].name), r);
  for (const [label, set] of ways) if (set.size < 2) ways.delete(label);
  return ways;
}

/**
 * One agent on one computer with two or more working ways into one service. A session has the agent's own servers,
 * its account's and its plugins' everywhere, and a project's only in that project: each project is weighed with
 * those, and says only what its own servers add.
 */
function doubledFlags(rows: ConnRow[], places: Map<string, string>): ConnFlag[] {
  const flags: ConnFlag[] = [];
  const groups = new Map<string, ConnRow[]>();
  for (const r of rows) if (r.place !== "board" && r.state === "ok") groups.set(`${r.place}|${r.part}`, [...(groups.get(`${r.place}|${r.part}`) ?? []), r]);
  for (const [key, list] of groups) {
    const [place, part] = key.split("|");
    const everywhere = list.filter((r) => !r.project);
    const projects = new Map<string, ConnRow[]>();
    for (const r of list) if (r.project) projects.set(r.project, [...(projects.get(r.project) ?? []), r]);
    const say = (ways: Map<string, Set<ConnRow>>, project: string | null) => {
      for (const [label, set] of ways) {
        const all = [...set];
        if (project && !all.some((r) => r.project === project)) continue;
        const names = all.map((r) => r.name);
        flags.push({
          id: `doubled:${place}:${part}:${project ?? ""}:${label}`,
          kind: "doubled",
          title: `${label}: ${set.size} ways in`,
          detail: `${part} on ${places.get(place) ?? "a computer"}${project ? `, in ${project},` : ""} reaches ${label} through ${names.slice(0, -1).join(", ")} and ${names.at(-1)}. One is usually enough.`,
          place,
          rows: all.map((r) => r.id),
        });
      }
    };
    say(waysIn(everywhere), null);
    for (const [project, own] of projects) say(waysIn([...everywhere, ...own]), project);
  }
  return flags;
}

export function connectionsReport(input: ConnInput): ConnReport {
  const computers = input.computers ?? [];
  const places: ConnPlace[] = [
    { id: "board", name: "Context Board", kind: "board", online: true, at: null, looking: null, reads: true, agents: {} },
    ...computers.map((c) => ({ id: c.id, name: c.host, kind: "computer" as const, online: c.online, at: c.at, looking: c.looking, reads: c.reads, agents: c.agents })),
  ];
  const placeName = new Map(places.map((p) => [p.id, p.name]));
  const rows = [...boardRows(input), ...computers.flatMap(computerRows)];
  // the groups in the order they are made (the board's) or by name (an agent's); within one, what needs you first
  const order = new Map(places.map((p, i) => [p.id, i]));
  const partAt = new Map<string, number>();
  rows.forEach((r, i) => !partAt.has(`${r.place}|${r.part}`) && partAt.set(`${r.place}|${r.part}`, i));
  const part = (r: ConnRow) => (r.place === "board" ? partAt.get(`${r.place}|${r.part}`)! : 0);
  rows.sort((a, b) => (order.get(a.place) ?? 0) - (order.get(b.place) ?? 0) || part(a) - part(b) || (a.place === "board" ? 0 : a.part.localeCompare(b.part)) || SEVERITY[a.state] - SEVERITY[b.state] || a.name.localeCompare(b.name));
  const flags: ConnFlag[] = [];
  for (const r of rows) {
    const where = r.place === "board" ? "Context Board" : `${r.part} on ${placeName.get(r.place) ?? "a computer"}`;
    if (r.state === "failed") flags.push({ id: `failed:${r.id}`, kind: "failed", title: `${r.name} is failing`, detail: `${where}. ${r.reason ?? ""}`.trim(), place: r.place, rows: [r.id] });
    else if (r.state === "signin") flags.push({ id: `signin:${r.id}`, kind: "signin", title: `${r.name} ${r.stateText === "Needs a key" ? "needs a key" : "waits for a sign-in"}`, detail: where, place: r.place, rows: [r.id] });
  }
  flags.push(...doubledFlags(rows, placeName));
  // a key that got into the board and has not since, or never did; an app the board has not read
  for (const k of input.keys) {
    const since = k.lastUsedAt ?? k.createdAt;
    if (input.now - since > UNUSED_MS) flags.push({ id: `unused:key:${k.id}`, kind: "unused", title: `${k.label} has not been used for ${Math.floor((input.now - since) / DAY)} days`, detail: k.lastUsedAt ? "Remove it if nothing needs it any more." : "It was never used. Remove it if nothing needs it.", place: "board", rows: [`key:${k.id}`] });
  }
  for (const c of input.connections)
    if (c.status === "ok" && c.lastUsedAt && input.now - c.lastUsedAt > UNUSED_MS) flags.push({ id: `unused:app:${c.id}`, kind: "unused", title: `${c.name} has not been read for ${Math.floor((input.now - c.lastUsedAt) / DAY)} days`, detail: "Remove it if no widget needs it any more.", place: "board", rows: [`app:${c.id}`] });
  const tone = rows.some((r) => r.state === "failed") ? "bad" : rows.some((r) => r.state === "signin") ? "warn" : "calm";
  return { places, rows, flags, total: rows.length, needYou: flags.length, tone };
}

/** The tile's one line: "41 connections · 4 need you". */
export function summaryLine(r: Pick<ConnReport, "total" | "needYou">): string {
  return `${r.total} ${r.total === 1 ? "connection" : "connections"}${r.needYou ? ` · ${r.needYou} need${r.needYou === 1 ? "s" : ""} you` : ""}`;
}
